import { useCallback, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import * as imagePreviewActions from '@/shared/modules/imagePreview/actions';
import config from '@/config';
import * as gameActions from '../store/actions';

import type { ReactNode } from 'react';
import type { RootState } from '@/rootState';
import type {
	GameAnswerEvent,
	GameAudioProps,
	GameCloseEvent,
	GameData,
	GameFinishEvent,
	GameFinishHandler,
	Question,
} from '../types';
import type { GameWrapperProps } from './GameWrapper';

type GameSessionRenderProps = GameAudioProps & {
	gameData: GameData;
	questions: Question[];
	onAnswer: (event: GameAnswerEvent) => void;
	onFinish: GameFinishHandler;
	onClose: (event?: GameCloseEvent) => void;
	onReplay: (gameData: GameData) => void;
	onImagePreview: (imageSrc: string) => void;
};

type GameSessionProps = Omit<GameWrapperProps, 'onFinish'> & {
	children: (props: GameSessionRenderProps) => ReactNode;
	onFinish?: GameFinishHandler;
	onClose?: (event?: GameCloseEvent) => void;
};

const GameSession = ({ children, onAnswer, onFinish, onClose, audioOn, onAudioChange }: GameSessionProps) => {
	const dispatch = useDispatch<any>();
	const { gameData, loading, questions } = useSelector(
		(state: RootState) => ({
			gameData: state.gameReducer.gameData,
			loading: state.gameReducer.loading,
			questions: state.gameReducer.questions,
		}),
		shallowEqual,
	);

	useEffect(() => {
		document.documentElement.classList.add('game-active');
		return () => {
			document.documentElement.classList.remove('game-active');
		};
	}, []);

	const handleFinish = useCallback(
		(event: GameFinishEvent, onFinished?: () => void) => {
			if (onFinish) {
				onFinish(event, onFinished);
				return;
			}

			onFinished?.();
		},
		[onFinish],
	);

	const handleAnswer = useCallback(
		(event: GameAnswerEvent) => {
			onAnswer?.(event);

			if (event.finishEvent) {
				handleFinish(event.finishEvent);
			}
		},
		[handleFinish, onAnswer],
	);

	const handleClose = useCallback(
		(event?: GameCloseEvent) => {
			if (onClose) {
				onClose(event);
				return;
			}
			if (event?.finishEvent) {
				handleFinish(event.finishEvent);
			}

			if (config.exit_url) {
				window.location.href = config.exit_url;
			}
		},
		[handleFinish, onClose],
	);

	const handleReplay = useCallback(
		(nextGameData: GameData) => {
			dispatch(gameActions.getGameDataSuccess(nextGameData));
		},
		[dispatch],
	);

	const handlePreviewImage = useCallback(
		(imageSrc: string) => {
			dispatch(
				imagePreviewActions.setImagePreviewModal({
					open: true,
					imageSrc,
				}),
			);
		},
		[dispatch],
	);

	if (loading || !gameData) return null;

	return children({
		gameData,
		questions,
		onAnswer: handleAnswer,
		onFinish: handleFinish,
		onClose: handleClose,
		onReplay: handleReplay,
		onImagePreview: handlePreviewImage,
		audioOn,
		onAudioChange,
	});
};

export default GameSession;
